// Phase SIKA-5b/2 — Panel 01: Goal Command (HORIZONTAL 3-column row).
//
// Source: video frames 001/003/010/015. Layout:
//   LEFT  — large progress arc with FUNDED %, background track visible at 0%
//   CENT  — NAMED GOAL eyebrow + serif title + huge gold amount + secured/at-risk line
//   RIGHT — Weeks remaining + Weekly required + Dynamic target (computed from goal state,
//           NOT from latestCycle — they render even when no cycle exists)
//
// Doctrine:
//   N01 — Renders cleanly with mode='simple' AND linked_chain_id=null.
//   N02 — Dynamic target recomputed on every render from current goal state.
//   N12 — Bankroll advisory NEVER blocks; surfaces as a small footer pill on the page,
//         not inside Panel 01 (the prop is preserved for backwards-compat tests).
//   Promotion-not-restriction copy throughout.

import { Eyebrow, Pill, Stat } from "../super-acca";
import { calculateSIKAOddsTarget } from "../../sika/calculateSIKAOddsTarget.ts";
import type { SikaGoalRow, SikaCycleRow } from "../../sika/useSikaGoal.ts";

export interface GoalCommandPanelProps {
  goal: SikaGoalRow | null;
  /** Pro tier? Pro adds the locked-feature explainer pill. */
  pro?: boolean;
  /** Latest published cycle — optional; if absent we still compute dynamic target. */
  latestCycle?: SikaCycleRow | null;
  /** Edit goal modal trigger — wired by the page. */
  onEditGoal?: () => void;
  /** N12 bankroll advisory banner — preserved for backwards-compat, rendered subtly. */
  bankrollAdvisory?: {
    breach: boolean;
    message: string;
  } | null;
}

function ghc(amount: number): string {
  return `GH₵${amount.toLocaleString("en-GH", { maximumFractionDigits: 0 })}`;
}

function weeksRemaining(deadline: string | null): number {
  if (!deadline) return 0;
  const ms = new Date(deadline).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.ceil(ms / (7 * 24 * 60 * 60 * 1000));
}

function totalWeeks(goal: SikaGoalRow): number {
  if (!goal.deadline) return 0;
  const created = new Date(goal.created_at).getTime();
  const end = new Date(goal.deadline).getTime();
  if (!Number.isFinite(end) || end <= created) return 0;
  return Math.ceil((end - created) / (7 * 24 * 60 * 60 * 1000));
}

function pctFunded(current: number, target: number): number {
  if (target <= 0) return 0;
  return Math.round((current / target) * 1000) / 10;
}

/**
 * Circular progress arc. CRITICAL: the grey background track ring is always
 * rendered (Gap #10 — previously invisible at 0%). The saffron arc on top
 * overlays from start to (start + 360*pct/100).
 */
function BigProgressArc({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(100, pct));
  const radius = 78;
  const stroke = 10;
  const cx = 100;
  const cy = 100;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - clamped / 100);
  const colour = clamped >= 80 ? "var(--sa-signal-green)" : "var(--sa-accent-saffron)";
  return (
    <div style={{ position: "relative", width: 200, height: 200 }} data-testid="sika-progress-arc">
      <svg width={200} height={200} viewBox="0 0 200 200" aria-hidden>
        {/* Background track — ALWAYS rendered, visible at all funded levels including 0% */}
        <circle
          data-testid="sika-progress-track"
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="var(--sa-hairline)"
          strokeWidth={stroke}
        />
        {/* Foreground filled arc */}
        <circle
          data-testid="sika-progress-fill"
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke={colour}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${String(cx)} ${String(cy)})`}
        />
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          textAlign: "center",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            fontFamily: "var(--sa-font-stat)",
            fontSize: 30,
            color: "var(--sa-accent-saffron)",
            letterSpacing: "0.02em",
          }}
        >
          {clamped.toFixed(1)}%
        </div>
        <div
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 9.5,
            color: "var(--sa-fg-muted)",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            marginTop: 2,
          }}
        >
          Funded
        </div>
      </div>
    </div>
  );
}

export function GoalCommandPanel({
  goal,
  pro = false,
  // latestCycle accepted for backwards-compat; not used for the right-column
  // dynamic target (we recompute from goal state per N02 / Gap #4).
  latestCycle: _latestCycle = null,
  onEditGoal,
  bankrollAdvisory = null,
}: GoalCommandPanelProps) {
  // Empty state — promotion-not-restriction copy
  if (!goal) {
    return (
      <div style={{ padding: 18 }} data-panel="goal-command">
        <Eyebrow>Panel 1 · Goal Command</Eyebrow>
        <div
          style={{
            marginTop: 12,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 13,
            color: "var(--sa-fg-muted)",
          }}
        >
          No active goal yet. Set your first goal — SIKA will build the plan, select
          the weekly intelligence, and track every cycle toward it.
        </div>
        <button
          type="button"
          style={{
            marginTop: 14,
            padding: "10px 18px",
            background: "var(--sa-accent-saffron)",
            color: "var(--sa-bg-primary)",
            border: "none",
            borderRadius: 3,
            fontFamily: "var(--sa-font-stat)",
            fontSize: 12,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            cursor: "pointer",
          }}
          data-action="set-first-goal"
        >
          Set your first goal
        </button>
      </div>
    );
  }

  const wks = weeksRemaining(goal.deadline);
  const total = totalWeeks(goal);
  const currentWeek = total > 0 ? Math.max(1, total - wks) : 0;
  const pct = pctFunded(goal.current_amount, goal.target_amount);
  const remaining = Math.max(0, goal.target_amount - goal.current_amount);
  const weeklyRequired = wks > 0 ? Math.round(remaining / wks) : 0;
  const banked = Number(goal.banked_amount ?? 0);
  const atRisk = Math.max(0, goal.current_amount - banked);

  // Gap #4 — Compute dynamic target from goal state directly, so the stat
  // renders meaningfully even with zero cycles.
  const targetResult = calculateSIKAOddsTarget({
    target_amount: goal.target_amount,
    current_amount: goal.current_amount,
    banked_amount: banked,
    weeks_remaining: wks,
  });
  const dynamicTarget = targetResult.dynamic_target;

  // Gap #2 — Mode mention preserved subtly but NO extra SIMPLE/ACTIVE badges
  // attached to the goal title (the MODE chip lives in GoalSelectorTabs).

  return (
    <div style={{ padding: 22 }} data-panel="goal-command" data-layout="horizontal">
      {/* Top eyebrow row */}
      <div style={{ marginBottom: 18 }}>
        <Eyebrow>— Panel 01 · Goal Command</Eyebrow>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(200px, 30%) minmax(0, 1fr) minmax(200px, 25%)",
          gap: 28,
          alignItems: "center",
        }}
      >
        {/* LEFT — big arc */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <BigProgressArc pct={pct} />
        </div>

        {/* CENTER — NAMED GOAL eyebrow + serif title + huge gold amount */}
        <div>
          <Eyebrow>Named Goal</Eyebrow>
          <div
            style={{
              marginTop: 6,
              fontFamily: "var(--sa-font-serif, Georgia, 'Times New Roman', serif)",
              fontSize: 36,
              letterSpacing: "0.005em",
              lineHeight: 1.05,
              color: "var(--sa-fg-primary)",
            }}
            data-testid="goal-title"
          >
            {goal.title}
          </div>
          <div style={{ marginTop: 14, display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <span
              style={{
                fontFamily: "var(--sa-font-stat)",
                fontSize: 44,
                color: "var(--sa-accent-saffron)",
                letterSpacing: "0.01em",
                lineHeight: 1,
              }}
              data-testid="goal-current-amount"
            >
              {ghc(goal.current_amount)}
            </span>
            <span
              style={{
                fontFamily: "var(--sa-font-mono)",
                fontSize: 13,
                color: "var(--sa-fg-muted)",
              }}
            >
              of {ghc(goal.target_amount)}
            </span>
          </div>
          <div
            style={{
              marginTop: 10,
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11.5,
              color: "var(--sa-fg-secondary)",
              lineHeight: 1.5,
            }}
            data-testid="goal-secured-line"
          >
            {ghc(banked)} secured — cannot be lost.
            {atRisk > 0 ? ` · ${ghc(atRisk)} active at risk this week.` : ""}
          </div>
        </div>

        {/* RIGHT — 3 stats stacked, computed from goal state */}
        <div style={{ display: "grid", gap: 14 }} data-testid="sika-right-stats">
          <Stat
            label="Weeks remaining"
            value={total > 0 ? `${String(currentWeek)} / ${String(total)}` : wks > 0 ? String(wks) : "—"}
          />
          <Stat
            label="Weekly required"
            value={weeklyRequired > 0 ? ghc(weeklyRequired) : "—"}
          />
          <Stat
            label="Dynamic target"
            value={`${dynamicTarget.toFixed(1)}×`}
            accent
          />
        </div>
      </div>

      {/* N12 — Bankroll advisory preserved for backwards-compat. Visible but
          subtle; the page-level footer pill is the primary surface. */}
      {bankrollAdvisory ? (
        <div
          role="status"
          data-testid="sika-bankroll-advisory"
          data-breach={bankrollAdvisory.breach}
          style={{
            marginTop: 18,
            padding: "8px 12px",
            border: `1px solid ${bankrollAdvisory.breach ? "var(--sa-accent-saffron)" : "var(--sa-hairline)"}`,
            borderRadius: 3,
            background: bankrollAdvisory.breach
              ? "rgba(255,200,64,0.08)"
              : "transparent",
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            lineHeight: 1.5,
            color: bankrollAdvisory.breach
              ? "var(--sa-accent-saffron)"
              : "var(--sa-fg-secondary)",
          }}
        >
          {bankrollAdvisory.message}
        </div>
      ) : null}

      {/* Edit goal — keep the data-testid for backwards-compat (sika3Panels test
          asserts on it). The visible "Edit goal" link in the tabs is the primary
          surface; this CTA is now a small inline action and only renders when
          the prop is wired. */}
      {onEditGoal ? (
        <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={onEditGoal}
            data-testid="sika-edit-goal-cta"
            style={{
              padding: "6px 12px",
              background: "transparent",
              color: "var(--sa-accent-saffron)",
              border: "1px solid var(--sa-accent-saffron)",
              borderRadius: 3,
              fontFamily: "var(--sa-font-mono)",
              fontSize: 10.5,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            Edit goal
          </button>
        </div>
      ) : null}

      {!pro ? (
        <div style={{ marginTop: 12 }}>
          <Pill>🔒 Pro adds banked breakdown + weekly required rate.</Pill>
        </div>
      ) : null}
    </div>
  );
}
